import axios from 'axios'
import { computed, ref } from 'vue'
import { BASE_URL, WEBPUSH_PUBLIC_KEY } from '@/environment/environment'
import { logtoClient } from '@/plugins/logto'
import type { ICustomAxiosRequestConfig } from '@/types/ICustomAxiosRequestConfig'

const VAPID_PUBLIC_KEY = WEBPUSH_PUBLIC_KEY || ''

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function detectBrowser(): string {
  const userData = (window.navigator as any).userAgentData
  if (userData?.brands?.length) {
    const brands = userData.brands as Array<{ brand: string; version: string }>
    const targetBrand = brands.find(b =>
      !b.brand.includes('Chromium')
      && !b.brand.includes('Not')
      && !b.brand.includes('Generic'),
    )
    if (targetBrand) return targetBrand.brand.toLowerCase()
  }

  const ua = window.navigator.userAgent.toLowerCase()
  if (ua.includes('samsungbrowser')) return 'samsung'
  if (ua.includes('miuibrowser')) return 'miui'
  if (ua.includes('yabrowser')) return 'yandex'
  if (ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr')) return 'chrome'
  if (ua.includes('firefox')) return 'firefox'
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari'
  if (ua.includes('edg')) return 'edge'
  if (ua.includes('opr') || ua.includes('opera')) return 'opera'
  return 'unknown'
}

const isSupported = ref(false)
const isSubscribed = ref(false)
const permission = ref<NotificationPermission>('default')

export function usePushSubscription() {
  const checkSupport = () => {
    isSupported.value = 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window
      && !!VAPID_PUBLIC_KEY
  }

  const checkSubscription = async () => {
    checkSupport()
    if (!isSupported.value) return

    permission.value = Notification.permission

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      isSubscribed.value = !!subscription
    } catch {
      isSubscribed.value = false
    }
  }

  const syncSubscriptionToBackend = async (subscription: PushSubscription): Promise<void> => {
    const subJson = subscription.toJSON()
    await axios.post(
      `${BASE_URL}/api/notifications/webpush/save-info/`,
      {
        status_type: 'subscribe',
        group: 'all',
        browser: detectBrowser(),
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: subJson.keys?.p256dh,
            auth: subJson.keys?.auth,
          },
        },
      },
      { skipAuthLogout: true } as ICustomAxiosRequestConfig,
    )
  }

  const subscribe = async (): Promise<boolean> => {
    if (!isSupported.value) return false

    try {
      const perm = await Notification.requestPermission()
      permission.value = perm
      if (perm !== 'granted') return false

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as any,
      })

      await syncSubscriptionToBackend(subscription)
      isSubscribed.value = true
      return true
    } catch (err) {
      console.error('[Push] Ошибка при оформлении подписки:', err)
      return false
    }
  }

  /** Идемпотентно восстанавливает/создаёт Web Push подписку. */
  const ensureSubscription = async (): Promise<void> => {
    // Без сессии sync/subscribe не имеют смысла и на /login провоцировали
    // getAccessToken fail → logout/signOut → portal↔Logto петлю.
    const isAuth = logtoClient ? await logtoClient.isAuthenticated() : false
    if (!isAuth) return

    checkSupport()
    if (!isSupported.value) return

    permission.value = Notification.permission

    let registration: ServiceWorkerRegistration | null = null
    try {
      registration = await navigator.serviceWorker.getRegistration() || null
      if (!registration) {
        registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<null>(resolve => {
            window.setTimeout(() => resolve(null), 1500)
          }),
        ])
      }
    } catch {
      return
    }

    if (!registration?.pushManager) return

    const existing = await registration.pushManager.getSubscription()

    if (existing) {
      try {
        await syncSubscriptionToBackend(existing)
        isSubscribed.value = true
      } catch (err) {
        console.error('[Push] Ошибка при автоматической синхронизации подписки:', err)
      }
      return
    }

    if (permission.value === 'denied') return

    await subscribe()
  }

  const unsubscribe = async (): Promise<boolean> => {
    try {
      if (!('serviceWorker' in navigator)) return false
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) return false

      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        const subJson = subscription.toJSON()
        await axios.post(
          `${BASE_URL}/api/notifications/webpush/save-info/`,
          {
            status_type: 'unsubscribe',
            group: 'all',
            browser: detectBrowser(),
            subscription: {
              endpoint: subscription.endpoint,
              keys: {
                p256dh: subJson.keys?.p256dh,
                auth: subJson.keys?.auth,
              },
            },
          },
          { skipAuthLogout: true } as ICustomAxiosRequestConfig,
        )
        await subscription.unsubscribe()
      }

      isSubscribed.value = false
      return true
    } catch (err) {
      console.error('[Push] Ошибка при отмене подписки:', err)
      return false
    }
  }

  const canSubscribe = computed(() => isSupported.value && permission.value !== 'denied')

  return {
    isSupported,
    isSubscribed,
    permission,
    canSubscribe,
    checkSubscription,
    subscribe,
    unsubscribe,
    ensureSubscription,
  }
}
